import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  IconTrophy,
  IconFlame,
  IconCode,
  IconTarget,
  IconBolt,
  IconArrowUpRight,
  IconSparkles,
  IconBriefcase,
  IconRoute,
  IconChevronRight,
  IconEye,
  IconBookmark,
  IconStarFilled,
  IconCrown,
  IconRocket,
  IconMedal,
} from '@tabler/icons-react'
import { supabase } from '@/services/supabase'
import { useAuth } from '@/context/auth-context'
import api from '@/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { ContributionHeatmap } from '@/components/candidate/contribution-heatmap'
import { ProfilePowerRing } from '@/components/candidate/profile-power-ring'
import { SkillRadar } from '@/components/candidate/skill-radar'
import { StreakTracker } from '@/components/candidate/streak-tracker'

export const Route = createFileRoute('/_authenticated/')({
  component: CandidateDashboard,
})

// ── XP Level Computation ──
function getLevel(xp: number) {
  if (xp >= 5000)
    return {
      level: 'Legend',
      tier: 5,
      emoji: '👑',
      gradient: 'from-amber-400 via-yellow-500 to-orange-500',
      next: Infinity,
      progress: 100,
    }
  if (xp >= 3000)
    return {
      level: 'Master',
      tier: 4,
      emoji: '💎',
      gradient: 'from-cyan-400 to-blue-500',
      next: 5000,
      progress: ((xp - 3000) / 2000) * 100,
    }
  if (xp >= 1500)
    return {
      level: 'Expert',
      tier: 3,
      emoji: '⚡',
      gradient: 'from-purple-400 to-indigo-500',
      next: 3000,
      progress: ((xp - 1500) / 1500) * 100,
    }
  if (xp >= 500)
    return {
      level: 'Rising Star',
      tier: 2,
      emoji: '🌟',
      gradient: 'from-green-400 to-emerald-500',
      next: 1500,
      progress: ((xp - 500) / 1000) * 100,
    }
  return {
    level: 'Code Cadet',
    tier: 1,
    emoji: '🌱',
    gradient: 'from-zinc-400 to-zinc-500',
    next: 500,
    progress: (xp / 500) * 100,
  }
}

function StatCard({
  icon: Icon,
  label,
  value,
  subtitle,
}: {
  icon: any
  label: string
  value: string | number
  subtitle: string
}) {
  return (
    <Card className='border-zinc-200/50 shadow-sm dark:border-zinc-800/50'>
      <CardContent className='p-5'>
        <div className='flex items-center justify-between'>
          <div>
            <p className='mb-1 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground'>
              {label}
            </p>
            <p className='text-2xl font-black tracking-tight text-zinc-900 dark:text-zinc-100'>
              {value}
            </p>
            <p className='mt-0.5 text-[11px] font-medium text-muted-foreground'>
              {subtitle}
            </p>
          </div>
          <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100 dark:bg-zinc-800'>
            <Icon className='h-6 w-6 text-zinc-400' />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Quick Action Card ──
function QuickAction({
  icon: Icon,
  title,
  description,
  href,
  gradient,
  soon,
}: {
  icon: any
  title: string
  description: string
  href: string
  gradient: string
  soon?: boolean
}) {
  return (
    <Link to={href}>
      <div
        className={`group relative cursor-pointer overflow-hidden rounded-2xl border border-zinc-200/50 bg-white p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md dark:border-zinc-800/50 dark:bg-zinc-900/50`}
      >
        <div className='relative z-10 flex items-start gap-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-100 dark:bg-zinc-800'>
            <Icon className='h-5 w-5 text-zinc-500' />
          </div>
          <div className='min-w-0 flex-1'>
            <div className='flex items-center gap-2'>
              <h4 className='text-sm font-bold text-zinc-900 dark:text-zinc-100'>
                {title}
              </h4>
              {soon && (
                <Badge
                  variant='outline'
                  className='h-4 border-amber-400/50 px-1.5 py-0 text-[9px] font-bold text-amber-500'
                >
                  SOON
                </Badge>
              )}
            </div>
            <p className='mt-0.5 line-clamp-1 text-xs text-zinc-500'>
              {description}
            </p>
          </div>
          <IconChevronRight className='mt-1 h-4 w-4 shrink-0 text-zinc-300 transition-colors group-hover:text-zinc-500 dark:text-zinc-600' />
        </div>
      </div>
    </Link>
  )
}

function CandidateDashboard() {
  const { user } = useAuth()

  // 100% Dynamic: Fetch End-to-End Metrics Engine from backend
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['candidate-metrics', user?.id],
    queryFn: async () => {
      const res = await api.get('/candidate/metrics');
      return res.data.data;
    },
    enabled: !!user?.id,
  })

  // Dynamic Recent Activity
  const { data: recentActivity, isLoading: isLoadingActivity } = useQuery({
    queryKey: ['candidate-activity', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('candidate_activity_logs')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(5)

      if (error) throw error
      return data || []
    },
    enabled: !!user?.id,
  })

  const levelInfo = getLevel(metrics?.points || 0)

  // 100% Dynamic: Fetch skills from backend
  const { data: skillData } = useQuery({
    queryKey: ['candidate-skills', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('candidate_skills')
        .select('name, proficiency')
        .eq('user_id', user?.id)
        .limit(6)

      if (error && error.code !== 'PGRST116') throw error
      
      return (data || []).map(skill => {
        let level = 25
        if (skill.proficiency === 'Intermediate') level = 50
        if (skill.proficiency === 'Advance') level = 75
        if (skill.proficiency === 'Professional') level = 100
        return { name: skill.name, level }
      })
    },
    enabled: !!user?.id,
  })

  // 100% Dynamic: Fetch badges from backend
  const { data: earnedBadges } = useQuery({
    queryKey: ['candidate-badges', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('candidate_badges')
        .select('badge_key, badge_label, badge_emoji, earned_at')
        .eq('user_id', user?.id)

      if (error && error.code !== 'PGRST116') throw error
      return data || []
    },
    enabled: !!user?.id,
  })

  // 100% Dynamic: Fetch contribution heatmap from backend
  const { data: contributions } = useQuery({
    queryKey: ['candidate-contributions', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('candidate_contributions')
        .select('activity_date, contribution_count')
        .eq('user_id', user?.id)

      if (error && error.code !== 'PGRST116') throw error
      // Convert to Record<string, number> for heatmap
      const map: Record<string, number> = {}
      if (data) {
        data.forEach((row: any) => {
          map[row.activity_date] = row.contribution_count
        })
      }
      return map
    },
    enabled: !!user?.id,
  })

  const activityIcons: Record<string, string> = {
    problem_solved: '💻',
    profile_updated: '✏️',
    badge_earned: '🏅',
    job_applied: '📤',
    contest_entered: '🏆',
    streak_milestone: '🔥',
    login: '👋',
  }

  if (isLoading || isLoadingActivity) {
    return (
      <div className='flex-1 space-y-6 p-4 pt-6 md:p-8'>
        <Skeleton className='h-10 w-[250px]' />
        <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-4'>
          <Skeleton className='h-28 rounded-2xl' />
          <Skeleton className='h-28 rounded-2xl' />
          <Skeleton className='h-28 rounded-2xl' />
          <Skeleton className='h-28 rounded-2xl' />
        </div>
        <div className='grid gap-4 md:grid-cols-3'>
          <Skeleton className='col-span-2 h-64 rounded-2xl' />
          <Skeleton className='h-64 rounded-2xl' />
        </div>
      </div>
    )
  }

  return (
    <div className='flex-1 space-y-6 p-4 pt-6 md:p-8'>
      {/* ── Hero Header ── */}
      <div className='flex flex-col items-start justify-between gap-4 md:flex-row md:items-center'>
        <div>
          <div className='mb-1 flex items-center gap-3'>
            <h2 className='text-2xl font-black tracking-tight text-zinc-900 dark:text-zinc-100 md:text-3xl'>
              Welcome back, {user?.first_name || 'Candidate'} 👋
            </h2>
          </div>
          <div className='mt-2 flex items-center gap-3'>
            <Badge variant='secondary' className='px-3 py-1 text-xs font-bold'>
              {levelInfo.emoji} {levelInfo.level}
            </Badge>
            <span className='text-sm font-medium text-zinc-500'>
              {metrics?.points?.toLocaleString() || 0} XP
            </span>
            {levelInfo.next !== Infinity && (
              <span className='text-xs text-zinc-400'>
                • {levelInfo.next - (metrics?.points || 0)} XP to next level
              </span>
            )}
          </div>
        </div>
        <div className='flex gap-2'>
          <Link to='/practice'>
            <Button
              size='sm'
              className='rounded-xl font-bold'
            >
              <IconCode className='mr-1.5 h-4 w-4' />
              Practice Now
            </Button>
          </Link>
          <Link to='/jobs'>
            <Button
              size='sm'
              variant='outline'
              className='rounded-xl font-bold hover:bg-zinc-50 dark:hover:bg-zinc-800'
            >
              <IconBriefcase className='mr-1.5 h-4 w-4' />
              Browse Jobs
            </Button>
          </Link>
        </div>
      </div>

      {/* ── XP Progress Bar ── */}
      {levelInfo.next !== Infinity && (
        <div className='relative'>
          <div className='mb-2 flex items-center justify-between'>
            <span className='text-xs font-bold uppercase tracking-wider text-zinc-400'>
              Level Progress
            </span>
            <span className='text-xs font-bold text-zinc-500'>
              {Math.round(levelInfo.progress)}%
            </span>
          </div>
          <div className='h-2.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800'>
            <div
              className='h-full rounded-full bg-primary transition-all duration-1000 ease-out'
              style={{ width: `${levelInfo.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* ── Stats Grid ── */}
      <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
        <StatCard
          icon={IconTrophy}
          label='Total XP'
          value={metrics?.points?.toLocaleString() || '0'}
          subtitle='Earn more by practicing'
        />
        <StatCard
          icon={IconFlame}
          label='Current Streak'
          value={`${metrics?.streak_days || 0} Days`}
          subtitle={
            metrics?.streak_days >= 7 ? "You're on fire!" : 'Start your streak'
          }
        />
        <StatCard
          icon={IconCode}
          label='Problems Solved'
          value={metrics?.problems_solved || 0}
          subtitle='Across all topics'
        />
        <StatCard
          icon={IconTarget}
          label='Global Rank'
          value={
            metrics?.global_rank === 'Unranked'
              ? '—'
              : `#${metrics?.global_rank}`
          }
          subtitle='Compete to rank up'
        />
      </div>

      {/* ── Main Content Grid ── */}
      <div className='grid gap-6 lg:grid-cols-3'>
        {/* ── Left Column: Contribution Heatmap ── */}
        <div className='space-y-6 lg:col-span-2'>
          {/* Contribution Heatmap */}
          <Card className='overflow-hidden border-zinc-200/50 shadow-sm dark:border-zinc-800/50'>
            <CardHeader className='pb-3'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2.5'>
                  <div className='flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-800'>
                    <IconSparkles className='h-4 w-4 text-zinc-500' />
                  </div>
                  <div>
                    <CardTitle className='text-sm font-bold'>
                      Activity Heatmap
                    </CardTitle>
                    <CardDescription className='text-xs'>
                      Your coding journey at a glance
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className='pt-0'>
              <ContributionHeatmap data={contributions} />
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div>
            <h3 className='mb-3 text-xs font-extrabold uppercase tracking-[0.2em] text-zinc-400'>
              Quick Actions
            </h3>
            <div className='grid gap-2 sm:grid-cols-2'>
              <QuickAction
                icon={IconCode}
                title='Daily Challenge'
                description="Solve today's problem to maintain your streak"
                href='/practice'
                gradient=''
              />
              <QuickAction
                icon={IconRoute}
                title='Career Roadmap'
                description='Follow your personalized learning path'
                href='/roadmaps'
                gradient=''
              />
              <QuickAction
                icon={IconTrophy}
                title='Leaderboards'
                description='See where you rank globally'
                href='/leaderboards'
                gradient=''
              />
              <QuickAction
                icon={IconRocket}
                title='AI Mock Interview'
                description='Practice with AI interviewer'
                href='/interviews'
                gradient=''
              />
            </div>
          </div>

          {/* Recruiter Visibility */}
          <Card className='border-zinc-200/50 shadow-sm dark:border-zinc-800/50'>
            <CardContent className='p-5'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-100 dark:bg-zinc-800'>
                    <IconEye className='h-5 w-5 text-zinc-500' />
                  </div>
                  <div>
                    <h4 className='text-sm font-bold text-zinc-900 dark:text-zinc-100'>
                      Recruiter Visibility
                    </h4>
                    <p className='mt-0.5 text-xs text-zinc-500'>
                      Your profile views this week
                    </p>
                  </div>
                </div>
                <div className='flex items-center gap-6'>
                  <div className='text-center'>
                    <p className='text-xl font-black text-zinc-900 dark:text-zinc-100'>
                      {metrics?.profile_views || 0}
                    </p>
                    <p className='text-[9px] font-bold uppercase tracking-wider text-zinc-400'>
                      Views
                    </p>
                  </div>
                  <div className='text-center'>
                    <p className='text-xl font-black text-zinc-900 dark:text-zinc-100'>
                      {metrics?.profile_saves || 0}
                    </p>
                    <p className='text-[9px] font-bold uppercase tracking-wider text-zinc-400'>
                      Saved
                    </p>
                  </div>
                  <div className='text-center'>
                    <p className='text-xl font-black text-zinc-900 dark:text-zinc-100'>
                      {metrics?.profile_matches || 0}
                    </p>
                    <p className='text-[9px] font-bold uppercase tracking-wider text-zinc-400'>
                      Matches
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Right Column ── */}
        <div className='space-y-6'>
          {/* Profile Power */}
          <Card className='border-zinc-200/50 shadow-sm dark:border-zinc-800/50'>
            <CardHeader className='pb-2'>
              <CardTitle className='flex items-center gap-2 text-sm font-bold'>
                <IconBolt className='h-4 w-4 text-amber-500' />
                Profile Power
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-0'>
              <ProfilePowerRing
                completeness={metrics?.profile_completeness || 20}
                size={140}
              />
              <div className='mt-4 space-y-2'>
                {(metrics?.profile_completeness || 20) < 100 && (
                  <Link to='/profile'>
                    <Button
                      variant='outline'
                      size='sm'
                      className='w-full rounded-xl text-xs font-bold hover:bg-zinc-50 dark:hover:bg-zinc-800'
                    >
                      <IconArrowUpRight className='mr-1.5 h-3.5 w-3.5' />
                      Complete Your Profile
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Streak Tracker */}
          <Card className='border-zinc-200/50 shadow-sm dark:border-zinc-800/50'>
            <CardHeader className='pb-2'>
              <CardTitle className='flex items-center gap-2 text-sm font-bold'>
                <IconFlame className='h-4 w-4 text-orange-500' />
                Streak
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-0'>
              <StreakTracker
                currentStreak={metrics?.streak_days || 0}
                longestStreak={metrics?.longest_streak || 0}
              />
            </CardContent>
          </Card>

          {/* Skill Radar */}
          <Card className='border-zinc-200/50 shadow-sm dark:border-zinc-800/50'>
            <CardHeader className='pb-2'>
              <CardTitle className='flex items-center gap-2 text-sm font-bold'>
                <IconStarFilled className='h-4 w-4 text-blue-500' />
                Skill Radar
              </CardTitle>
              <CardDescription className='text-xs'>
                Your technical proficiency
              </CardDescription>
            </CardHeader>
            <CardContent className='flex justify-center pt-0'>
              {skillData && skillData.length > 0 ? (
                <SkillRadar skills={skillData} size={220} />
              ) : (
                <div className='flex flex-col items-center justify-center py-8 text-center'>
                  <div className='mb-2 flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-100 dark:bg-zinc-800'>
                    <IconStarFilled className='h-5 w-5 text-zinc-300 dark:text-zinc-600' />
                  </div>
                  <p className='text-xs font-medium text-zinc-400'>
                    No skills tracked yet
                  </p>
                  <p className='mt-1 text-[10px] text-zinc-400'>
                    Upload your resume to auto-detect skills
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className='border-zinc-200/50 shadow-sm dark:border-zinc-800/50'>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm font-bold'>
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-0'>
              {recentActivity && recentActivity.length > 0 ? (
                <div className='space-y-3'>
                  {recentActivity.map((activity: any) => (
                    <div
                      key={activity.id}
                      className='group flex items-start gap-3'
                    >
                      <span className='mt-0.5 shrink-0 text-sm'>
                        {activityIcons[activity.action_type] || '📌'}
                      </span>
                      <div className='min-w-0 flex-1'>
                        <p className='text-xs font-medium leading-snug text-zinc-700 dark:text-zinc-300'>
                          {activity.action_type
                            ?.replace(/_/g, ' ')
                            ?.replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </p>
                        <p className='mt-0.5 text-[10px] text-zinc-400'>
                          {new Date(activity.created_at).toLocaleDateString(
                            'en-US',
                            {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            }
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='flex flex-col items-center justify-center py-8 text-center'>
                  <div className='mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100 dark:bg-zinc-800'>
                    <IconTarget className='h-6 w-6 text-zinc-300 dark:text-zinc-600' />
                  </div>
                  <p className='text-xs font-medium text-zinc-400'>
                    No activity yet
                  </p>
                  <p className='mt-1 text-[10px] text-zinc-400'>
                    Start solving problems to see your activity
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Badges Earned */}
          <Card className='border-zinc-200/50 shadow-sm dark:border-zinc-800/50'>
            <CardHeader className='pb-2'>
              <div className='flex items-center justify-between'>
                <CardTitle className='flex items-center gap-2 text-sm font-bold'>
                  <IconMedal className='h-4 w-4 text-amber-500' />
                  Badges
                </CardTitle>
                <Badge variant='secondary' className='text-[10px] font-bold'>
                  {metrics?.badges_earned || 0} earned
                </Badge>
              </div>
            </CardHeader>
            <CardContent className='pt-0'>
              {earnedBadges && earnedBadges.length > 0 ? (
                <div className='grid grid-cols-4 gap-2'>
                  {earnedBadges.map((badge: any) => (
                    <div
                      key={badge.badge_key}
                      className='flex flex-col items-center gap-1 rounded-xl p-2 transition-all cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-900/10'
                    >
                      <span className='text-xl'>{badge.badge_emoji}</span>
                      <span className='text-center text-[8px] font-bold uppercase leading-tight tracking-wide text-zinc-500'>
                        {badge.badge_label}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='flex flex-col items-center justify-center py-6 text-center'>
                  <div className='mb-2 flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-100 dark:bg-zinc-800'>
                    <IconMedal className='h-5 w-5 text-zinc-300 dark:text-zinc-600' />
                  </div>
                  <p className='text-xs font-medium text-zinc-400'>
                    No badges earned yet
                  </p>
                  <p className='mt-1 text-[10px] text-zinc-400'>
                    Start practicing to earn your first badge
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
